import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, switchMap } from 'rxjs';
import { SavedCartsRequest, SetVoucherRequest, SetVoucherRequestConfig } from './cart-aux.model';
import {
  InterceptorUtil,
  OCC_USER_ID_ANONYMOUS,
  OccEndpointsService,
  TranslationService,
  USE_CLIENT_TOKEN,
  UserIdService,
} from '@spartacus/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { failStatus, successStatus } from '../../../../core/models/request-status.model';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { CustomActiveCartService } from './active-cart.service';
import { Cart } from '@spartacus/cart/base/root';

@Injectable({
  providedIn: 'root',
})
export class CartAuxAdapter {
  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private occEndPointService: OccEndpointsService,
    private userFacede: UserAccountFacade,
    private userIdService: UserIdService,
    private activeCartService: CustomActiveCartService,
  ) {}

  protected getHeaders(userId: string): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (userId === OCC_USER_ID_ANONYMOUS) {
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
    }

    return headers;
  }

  setVoucher(config: SetVoucherRequestConfig): Observable<SetVoucherRequest> {
    const url = this.occEndPointService.buildUrl('cartVoucher', {
      urlParams: { userId: config.userID, cartId: config.cartID },
    });

    const toAdd = JSON.stringify({});
    const params: HttpParams = new HttpParams().set('voucherId', config.voucherCode);
    const headers = this.getHeaders(config.userID);

    return this.http.post(url, toAdd, { headers, params }).pipe(
      map(() => {
        return {
          ...successStatus,
        };
      }),
      catchError((error: any) => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            message: error?.error?.errors[0]?.message || textError,
            ...failStatus,
          })),
        );
      }),
    );
  }

  setEmails(emails: string[]): Observable<any> {
    return combineLatest([this.userIdService.getUserId(), this.activeCartService.getActive()]).pipe(
      switchMap(([userId, cart]) => {
        const url = this.occEndPointService.buildUrl('setEmails', {
          urlParams: { userId, cartId: cart.code },
        });

        const params: HttpParams = new HttpParams().set('emails', emails.join(','));
        const headers = this.getHeaders(userId);

        return this.http.post(url, '', { headers, params }).pipe(
          map(() => {
            return {
              ...successStatus,
            };
          }),
          catchError((error: any) => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: error?.error?.errors[0]?.message || textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }

  getSavedCart(): Observable<SavedCartsRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('savedCarts', {
          urlParams: { userId },
        });

        return this.http.get(url).pipe(
          map((data: any) => {
            const carts = (data.carts || []).sort((a: Cart, b: Cart) => {
              const date1: number = a.saveTime ? new Date(a.saveTime).getTime() : new Date().getTime();
              const date2: number = b.saveTime ? new Date(b.saveTime).getTime() : new Date().getTime();
              return date2 - date1;
            });

            return {
              ...successStatus,
              carts,
            };
          }),
          catchError((error: any) => {
            // eslint-disable-next-line no-console
            console.error(error?.error?.errors[0]?.message);

            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }

  removeSavedCart(cartId: string): Observable<any> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('deleteSavedCart', {
          urlParams: { userId, cartId },
        });

        return this.http.delete(url).pipe(
          map(() => {
            return {
              ...successStatus,
            };
          }),
          catchError((error: any) => {
            // eslint-disable-next-line no-console
            console.error(error?.error?.errors[0]?.message);

            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }
}
